import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BasicTable from "../../../Components/Table/Material";
import useApi from "../../../Hooks/useApi";
import Tooltip from "@mui/material/Tooltip";
import { usePagination } from "../../../Hooks/usePagination";

import { SVG_DATA_UPLOAD, SVG_NDF } from "../../../Assets/SVG/svg_util";
import Common from "../../../Components/Layouts/Common";
import showToast from "../../../Helpers/toast";

// =========================
// NDF STATUS HELPERS
// =========================

/**
 * All values that mean "NDF submission is fully done".
 * Covers whichever value the backend eventually settles on.
 */
const NDF_DONE_STATUSES = ["Success", "Completed", "Submitted"];

const isNdfDone = (ndfStatus) => NDF_DONE_STATUSES.includes(ndfStatus);

const isNdfInProgress = (ndfStatus) => ndfStatus === "In_Progress";

// =========================
// NDF STATUS OVERLAY ICONS
// =========================

const GreenTickOverlay = () => (
  <span
    className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-green-500 flex items-center justify-center shadow"
    title="Completed"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 12 12"
      fill="none"
      className="w-2.5 h-2.5"
    >
      <path
        d="M2 6.5L4.5 9L10 3.5"
        stroke="white"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);

const TimerOverlay = () => (
  <span
    className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-yellow-400 flex items-center justify-center shadow"
    title="In Progress"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 12 12"
      fill="none"
      className="w-2.5 h-2.5"
    >
      <circle cx="6" cy="6.5" r="4.5" stroke="white" strokeWidth="1.5" />
      <path
        d="M6 4V6.5L7.5 7.5"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4.5 1H7.5"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  </span>
);

// =========================
// NDF BUTTON HELPERS
// =========================

/**
 * Clickable only when data has been uploaded AND NDF submission
 * has not started or already completed.
 */
const isNdfClickable = (row) =>
  row.status === "SUBMITTED" &&
  !isNdfDone(row.ndfStatus) &&
  !isNdfInProgress(row.ndfStatus);

const getNdfBgColor = (row) =>
  isNdfClickable(row) ? "bg-[#1976d2]" : "bg-gray-400 cursor-not-allowed";

const getNdfTooltip = (row) => {
  if (isNdfDone(row.ndfStatus)) return "Already submitted to NDF";
  if (isNdfInProgress(row.ndfStatus)) return "NDF submission in progress";
  if (row.status !== "SUBMITTED")
    return "Upload data first before submitting to NDF";
  return "Submit to NDF";
};

const SurveyDataSubmissionCityList = () => {
  const { t } = useTranslation();
  const { request } = useApi();
  const navigate = useNavigate();
  const paginateProps = usePagination();

  const location = useLocation();
  const { surveyId, surveyName, questionId } = location?.state || {};

  const [surveyCityList, setSurveyCityList] = useState([]);
  const [data, setData] = useState(null);

  // =========================
  // DATA UPLOAD BUTTON CONFIG
  // =========================
  const dataUploadConfig = {
    Icon: SVG_DATA_UPLOAD,
    tooltip: (row) =>
      row.status === "SUBMITTED" ? "Already Uploaded" : "Upload Data",

    bgColor: (row) =>
      row.status === "SUBMITTED"
        ? "bg-gray-400 cursor-not-allowed"
        : "bg-[#1976d2]",

    onClick: (navigate, row) => () => {
      if (row.status !== "SUBMITTED") {
        navigate(`/survey-data-submission`, {
          state: {
            surveyId,
            surveyName,
            city: row.name,
            id: row.id,
            questionId,
            answerType: data?.answerType,
            locationId: row.id,
          },
        });
      }
    },
  };

  // =========================
  // FETCH
  // =========================
  const fetchSurveyCityList = async () => {
    try {
      const response = surveyId
        ? await request(
            "GET",
            `/v1/survey/city-mapping/${surveyId}`,
            {},
            "survey_service",
          )
        : undefined;

      setData(response);
      setSurveyCityList(response?.cities || []);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchSurveyCityList();
  }, []);

  // =========================
  // SUBMIT TO NDF
  // =========================
  const submitToNDF = async (payload) => {
    try {
      const response = await request(
        "POST",
        `/v1/external/ndf-publication/surveys/${payload.surveyId}/locations/${payload.locationId}`,
        {},
        "survey_service",
      );

      showToast({
        message: "Successfully submitted to NDF.",
      });

      if (response) {
        setTimeout(() => {
          fetchSurveyCityList();
        }, 30000);
      }
    } catch (error) {
      console.log(error?.response?.data?.message);
      showToast({
        message: "NDF Submission Failed!",
        type: "error",
      });
    }
  };

  // =========================
  // COLUMNS
  // =========================
  const columns = [
    {
      id: "name",
      label: "City Name",
      minWidth: 170,
    },
    {
      id: "creationDate",
      label: "Assigned Date",
      minWidth: 100,
    },
    {
      id: "status",
      label: "Data Submission Status",
      minWidth: 100,
    },

    // Action column — data upload
    {
      id: "action",
      label: "Actions",
      minWidth: 170,
      align: "center",
      renderCell: (row) => (
        <div className="inline-flex space-x-2">
          <Tooltip title={dataUploadConfig.tooltip(row)}>
            <button
              onClick={dataUploadConfig.onClick(navigate, row)}
              className={`${dataUploadConfig.bgColor(
                row,
              )} border border-gray-300 h-8 w-8 text-white rounded-md flex items-center justify-center p-1`}
            >
              <SVG_DATA_UPLOAD />
            </button>
          </Tooltip>
        </div>
      ),
    },

    // NDF Submission column
    {
      id: "ndfSubmission",
      label: "NDF Submission",
      minWidth: 170,
      align: "center",
      renderCell: (row) => {
        const clickable = isNdfClickable(row);

        const handleClick = () => {
          if (clickable) {
            submitToNDF({
              surveyId,
              surveyName,
              locationId: row.id,
              ndfStatus: row.ndfStatus,
              cityName: `${row.name} ${row.type}`,
            });
          }
        };

        return (
          <div className="inline-flex space-x-2">
            <Tooltip title={getNdfTooltip(row)}>
              {/*
                Wrapper span needed because MUI Tooltip won't attach to
                a disabled button. We manage disabled state via onClick guard
                and CSS instead of the native disabled attribute.
              */}
              <span className="relative inline-flex">
                <button
                  onClick={handleClick}
                  disabled={!clickable}
                  className={`
                    ${getNdfBgColor(row)}
                    border border-gray-300
                    h-8 w-8
                    text-white
                    rounded-md
                    flex items-center justify-center
                    p-1
                    transition-all duration-100
                    ${
                      clickable
                        ? "active:scale-90 active:brightness-90 active:shadow-inner"
                        : ""
                    }
                  `}
                >
                  <SVG_NDF />
                </button>

                {/* Status overlays */}
                {isNdfDone(row.ndfStatus) && <GreenTickOverlay />}
                {isNdfInProgress(row.ndfStatus) && <TimerOverlay />}
              </span>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    if (!surveyId) {
      navigate("/manage-survey");
    }
  }, [surveyId]);

  return (
    <Common headline={"Survey Data Submission City List"}>
      <div className="bg-white relative">
        <div className="flex mb-4">
          <h4 className="font-bold text-gray-800 pr-2">Survey Name:</h4>
          <h5>{surveyName}</h5>
        </div>

        <BasicTable
          columns={columns}
          data={surveyCityList}
          paginateProps={paginateProps}
        />
      </div>
    </Common>
  );
};

export default SurveyDataSubmissionCityList;
